// Market Target settings + alert ladder. Requirements 2, 5, 9.

import { useCallback, useEffect, useState } from "react";

import { useApi } from "../api/useApi";
import { ApiClientError } from "../api/client";
import { ChangeSecuritySection } from "../components/ChangeSecuritySection";
import { LadderEditor, validateLadder } from "../components/LadderEditor";
import type {
  AlertLevelInput,
  DrawdownState,
  MarketTarget,
  PolicyVersion,
  PriceBasis,
} from "../types";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

function toInputs(levels: PolicyVersion["alert_levels"]): AlertLevelInput[] {
  return levels.map((l) => ({
    id: l.id,
    drawdown_percent: l.drawdown_percent,
    maximum_margin_percent: l.maximum_margin_percent,
    enabled: l.enabled,
    conditional: l.conditional,
    label: l.label,
    display_order: l.display_order,
    notes: l.notes,
  }));
}

/** A labelled key/value row used in the target and headroom detail grids. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium tabular-nums">{children}</dd>
    </div>
  );
}

const price = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

function percent(value: number | null | undefined): string {
  return value == null ? "—" : `${value.toFixed(2)}%`;
}

function MarketTargetSkeleton() {
  return (
    <section className="space-y-6" aria-busy="true" aria-label="Loading market target">
      <Card>
        <CardHeader className="space-y-2 pb-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <Skeleton className="h-4 w-64 max-w-full" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-x-5 gap-y-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div className="space-y-1.5" key={index}>
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-5 w-20" />
              </div>
            ))}
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
          <Skeleton className="h-9 w-full" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex-row items-center justify-between pb-3">
          <div className="space-y-2">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-4 w-52" />
          </div>
          <Skeleton className="h-8 w-24" />
        </CardHeader>
        <CardContent className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton className="h-12 w-full" key={index} />
          ))}
        </CardContent>
      </Card>
    </section>
  );
}

export function MarketTargetPage() {
  const api = useApi();
  const [target, setTarget] = useState<MarketTarget | null>(null);
  const [state, setState] = useState<DrawdownState | null>(null);
  const [activePolicy, setActivePolicy] = useState<PolicyVersion | null>(null);
  const [draft, setDraft] = useState<AlertLevelInput[]>([]);
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [midDrawdownWarning, setMidDrawdownWarning] = useState<string | null>(null);
  const [pendingVersion, setPendingVersion] = useState<number | null>(null);
  // Empty-state create form: shown when the user has no market target yet.
  const [noTarget, setNoTarget] = useState(false);
  const [newSymbol, setNewSymbol] = useState("");
  const [newPriceBasis, setNewPriceBasis] = useState<PriceBasis>("unadjusted_close");
  const [creating, setCreating] = useState(false);
  const [editingLadder, setEditingLadder] = useState(false);
  const [changingSecurity, setChangingSecurity] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const targets = await api.listMarketTargets();
      if (targets.length === 0) {
        setNoTarget(true);
        setStatus("");
        return;
      }
      setNoTarget(false);
      const mt = targets[0];
      const [st, policies] = await Promise.all([
        api.getState(mt.market_target_id),
        api.listPolicies(mt.market_target_id),
      ]);
      // Publish the target and its dependent data together so the page never
      // renders a misleading empty ladder while the remaining requests finish.
      setTarget(mt);
      setState(st);
      const active = policies.find((p) => p.status === "active") ?? null;
      setActivePolicy(active);
      setDraft(active ? toInputs(active.alert_levels) : []);
      setStatus("");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const createTarget = async () => {
    const symbol = newSymbol.trim().toUpperCase();
    if (!symbol) {
      setStatus("Enter a ticker symbol to monitor.");
      return;
    }
    setCreating(true);
    setStatus("");
    try {
      await api.createMarketTarget({ symbol, price_basis: newPriceBasis });
      setNewSymbol("");
      // Reload so the full target view (state, policy, ladder) renders.
      await load();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not create market target");
    } finally {
      setCreating(false);
    }
  };

  const inDrawdown = state !== null && state.latest_close < state.all_time_high;

  const validation = validateLadder(
    draft,
    activePolicy?.ordinary_margin_ceiling,
    activePolicy?.absolute_margin_ceiling,
  );

  const saveDraftAndActivate = async (confirm: boolean) => {
    if (!target) return;
    setMidDrawdownWarning(null);
    try {
      // Create the draft, then activate it (confirming only when asked to).
      const created =
        pendingVersion === null
          ? await api.createDraftPolicy(target.market_target_id, { alert_levels: draft })
          : null;
      const version = pendingVersion ?? created!.policy.version;
      setPendingVersion(version);

      const result = await api.activatePolicy(target.market_target_id, version, {
        confirm,
        reason: confirm ? "Activated from dashboard" : undefined,
      });
      setStatus(`Activated policy version ${result.policy.version}.`);
      setPendingVersion(null);
      setEditingLadder(false);
      await load();
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        const body = err.body as { warning?: string } | null;
        setMidDrawdownWarning(
          body?.warning ?? "Changing the ladder during a drawdown is discouraged.",
        );
        return;
      }
      setStatus(err instanceof Error ? err.message : "Activation failed");
    }
  };

  if (loading && (!target || !state)) {
    return <MarketTargetSkeleton />;
  }

  if (noTarget && !target) {
    return (
      <section className="mx-auto max-w-md space-y-6">
        <p className="text-muted-foreground">
          No market target configured yet. Add a security to start monitoring it.
        </p>
        <Card>
          <CardContent className="pt-6">
            <form
              className="space-y-4"
              data-testid="create-target-form"
              onSubmit={(e) => {
                e.preventDefault();
                void createTarget();
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="new-symbol">Symbol</Label>
                <Input
                  id="new-symbol"
                  value={newSymbol}
                  onChange={(e) => setNewSymbol(e.target.value)}
                  aria-label="new-symbol"
                  placeholder="e.g. SPY"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-price-basis">Price basis</Label>
                <select
                  id="new-price-basis"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={newPriceBasis}
                  onChange={(e) => setNewPriceBasis(e.target.value as PriceBasis)}
                  aria-label="new-price-basis"
                >
                  <option value="unadjusted_close">Unadjusted closing price</option>
                  <option value="split_adjusted_close">Split-adjusted closing price</option>
                  <option value="total_return_close">Total-return closing price</option>
                </select>
              </div>
              <Button
                type="submit"
                disabled={creating || !newSymbol.trim()}
                data-testid="create-target-button"
              >
                {creating ? "Creating…" : "Create market target"}
              </Button>
            </form>
            {status && (
              <p className="status mt-3 text-sm text-muted-foreground">{status}</p>
            )}
          </CardContent>
        </Card>
      </section>
    );
  }

  if (status && !target) {
    return (
      <section className="space-y-2">
        <p className="text-muted-foreground">{status}</p>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      {target && state && (
        <Card data-testid={state.margin_headroom ? "margin-headroom" : undefined}>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">{target.symbol}</span>
                  <Badge variant={target.enabled ? "default" : "secondary"}>
                    {target.enabled ? "Monitoring" : "Disabled"}
                  </Badge>
                </CardTitle>
                <CardDescription className="mt-1 truncate" title={target.name}>
                  {target.name}
                </CardDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-expanded={changingSecurity}
                onClick={() => setChangingSecurity((value) => !value)}
              >
                {changingSecurity ? "Cancel" : "Change"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-5 gap-y-3 lg:grid-cols-4">
              <Field label="Latest close">{price.format(state.latest_close)}</Field>
              <Field label="Drawdown">{state.current_drawdown_percent.toFixed(2)}%</Field>
              <Field label="Current / permitted LTV">
                <span data-testid="current-ltv">
                  {percent(state.margin_headroom?.current_ltv_percent)}
                </span>{" "}
                /{" "}
                <span data-testid="permitted-margin">
                  {percent(state.margin_headroom?.permitted_margin_percent)}
                </span>
              </Field>
              <Field label="Headroom">
                <span data-testid="headroom">
                  {percent(state.margin_headroom?.headroom_percent)}
                </span>
              </Field>
            </dl>

            <p className="text-xs text-muted-foreground">
              ATH {price.format(state.all_time_high)} on {state.all_time_high_date}
              {state.margin_headroom?.reached_level_label
                ? ` · Current tier ${state.margin_headroom.reached_level_label}`
                : ""}
              {` · Latest session ${state.latest_close_date}`}
            </p>

            {state.margin_headroom?.current_ltv_percent != null && (
              <div>
                <div className="mb-1 flex items-center justify-between gap-4 text-[11px]">
                  <span className="font-medium">Current LTV vs permitted limit</span>
                  <span className="tabular-nums text-muted-foreground">
                    {state.margin_headroom.current_ltv_percent.toFixed(2)}% / {state.margin_headroom.permitted_margin_percent.toFixed(2)}%
                  </span>
                </div>
                <div className="relative h-2 overflow-hidden rounded-full bg-muted">
                  <span
                    className={`block h-full ${state.margin_headroom.over_limit ? "bg-destructive" : "bg-success"}`}
                    style={{
                      width: `${Math.min(100, (state.margin_headroom.current_ltv_percent / Math.max(state.margin_headroom.current_ltv_percent, state.margin_headroom.permitted_margin_percent, 1)) * 100)}%`,
                    }}
                  />
                  <span
                    className="absolute inset-y-0 w-0.5 bg-foreground"
                    style={{
                      left: `calc(${Math.min(100, (state.margin_headroom.permitted_margin_percent / Math.max(state.margin_headroom.current_ltv_percent, state.margin_headroom.permitted_margin_percent, 1)) * 100)}% - 1px)`,
                    }}
                    title={`Permitted limit ${state.margin_headroom.permitted_margin_percent.toFixed(2)}%`}
                  />
                </div>
              </div>
            )}

            {state.margin_headroom?.over_limit && (
              <Alert variant="destructive" data-testid="over-limit-warning">
                <AlertDescription>
                  Current margin exceeds the limit for this drawdown.
                </AlertDescription>
              </Alert>
            )}
            {state.margin_headroom?.current_ltv_percent == null && (
              <p className="text-sm text-muted-foreground">
                Link a brokerage account to compare current LTV with this limit.
              </p>
            )}

            <details className="rounded-md border border-border/70 px-3 py-2">
              <summary className="cursor-pointer text-sm font-medium">Security details</summary>
              <dl className="target-fields mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Field label="Exchange">{target.exchange}</Field>
                <Field label="Asset type">{target.asset_type}</Field>
                <Field label="Provider">{target.provider_id}</Field>
                <Field label="Price basis">{target.price_basis.replaceAll("_", " ")}</Field>
                <Field label="Market time zone">{target.market_timezone}</Field>
                <Field label="Last validated">
                  {target.last_session_date ?? state.latest_close_date}
                </Field>
              </dl>
            </details>

            <ChangeSecuritySection
              target={target}
              onChanged={load}
              expanded={changingSecurity}
              onExpandedChange={setChangingSecurity}
              showTrigger={false}
            />
          </CardContent>
        </Card>
      )}

      <Card className="gap-3 py-4 sm:gap-6 sm:py-6">
        <CardHeader className="px-4 pb-1 sm:px-6 sm:pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg">Alert ladder</CardTitle>
              {activePolicy && (
                <CardDescription className="mt-1">
                  Policy v{activePolicy.version} · {activePolicy.ordinary_margin_ceiling}% standard · {activePolicy.absolute_margin_ceiling}% absolute
                </CardDescription>
              )}
            </div>
            <Button
              type="button"
              variant={editingLadder ? "ghost" : "outline"}
              size="sm"
              aria-expanded={editingLadder}
              onClick={() => {
                if (editingLadder && activePolicy) setDraft(toInputs(activePolicy.alert_levels));
                setEditingLadder((value) => !value);
                setMidDrawdownWarning(null);
              }}
            >
              {editingLadder ? "Cancel" : "Edit ladder"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 px-4 sm:space-y-4 sm:px-6">
          <LadderEditor
            levels={draft}
            onChange={setDraft}
            allTimeHigh={state?.all_time_high}
            editable={editingLadder}
            ordinaryCeiling={activePolicy?.ordinary_margin_ceiling}
            absoluteCeiling={activePolicy?.absolute_margin_ceiling}
            footerAction={
              editingLadder ? (
                <Button
                  type="button"
                  disabled={!validation.canActivate}
                  onClick={() => saveDraftAndActivate(false)}
                  data-testid="activate-button"
                >
                  Review &amp; activate
                </Button>
              ) : undefined
            }
          />

          {editingLadder && inDrawdown && (
            <Alert data-testid="in-drawdown-notice">
              <AlertDescription>
                The monitored security is currently below its all-time closing high.
                Changing the ladder now may require explicit confirmation.
              </AlertDescription>
            </Alert>
          )}

          {midDrawdownWarning && (
            <Alert variant="destructive" data-testid="mid-drawdown-warning">
              <AlertTitle>Confirm ladder change</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>{midDrawdownWarning}</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => saveDraftAndActivate(true)}
                  >
                    Activate immediately (confirm)
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setMidDrawdownWarning(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {status && <p className="status text-sm text-muted-foreground">{status}</p>}
        </CardContent>
      </Card>
    </section>
  );
}
